import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera } from "lucide-react";

type Props = {
  path: string;
  customerName: string;
  completedAt?: string | null;
  lat?: number | null;
  lng?: number | null;
  compact?: boolean;
};

/** Owner-facing proof of service: signed photo plus when/where it was taken. */
export default function ProofPhotoButton({
  path,
  customerName,
  completedAt,
  lat,
  lng,
  compact,
}: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const show = async () => {
    setLoading(true);
    const { data, error } = await supabase.storage.from("job-photos").createSignedUrl(path, 300);
    setLoading(false);
    if (error || !data?.signedUrl) {
      toast.error("Could not load the photo");
      return;
    }
    setUrl(data.signedUrl);
    setOpen(true);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        disabled={loading}
        onClick={show}
        className={compact ? "h-6 shrink-0 px-1.5 text-[10px]" : ""}
      >
        <Camera className={compact ? "h-3 w-3" : "mr-2 h-4 w-4"} />
        {compact ? "" : "Proof photo"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Proof of service — {customerName}</DialogTitle>
            <DialogDescription>
              {completedAt ? `Completed ${new Date(completedAt).toLocaleString()}` : "Completed"}
              {lat != null && lng != null ? ` · GPS ${lat.toFixed(5)}, ${lng.toFixed(5)}` : ""}
            </DialogDescription>
          </DialogHeader>
          {url && (
            <img
              src={url}
              alt={`Completed lawn service at ${customerName}`}
              className="w-full rounded-md border"
            />
          )}
          {lat != null && lng != null && (
            <a
              className="text-xs text-primary underline"
              target="_blank"
              rel="noreferrer"
              href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
            >
              Open photo location in Maps
            </a>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
